import type { CoreTextProps } from "./CoreText.types";

/**
 * CoreText - Foundation typography component for the goo-ds design system
 *
 * Renders text with consistent typography styles defined in Figma.
 * Styles are applied via CSS classes from style-tokens.css (token-first per Constitution).
 *
 * @example
 * ```tsx
 * <CoreText style="headline-lg-bold">Page Title</CoreText>
 * <CoreText style="body-md-regular" align="center">Body text</CoreText>
 * <CoreText style="link-md-medium" as="a" href="/page">Link text</CoreText>
 * ```
 */
export function CoreText({
  children,
  style = "body-md-regular",
  colorTokenName = "--coreText-text-primary-default",
  align = "left",
  className = "",
  as: Component = "p",
}: CoreTextProps) {
  // Alignment classes (structural utility per Constitution IV)
  const alignmentClass =
    align === "center" ? "text-center" : align === "right" ? "text-right" : "text-left";

  // Combine style class from style-tokens.css with alignment and custom classes
  const combinedClassName = [style, alignmentClass, className].filter(Boolean).join(" ");

  return (
    <Component
      className={combinedClassName}
      style={{
        color: `var(--gds-core-text-color, var(${colorTokenName}))`,
        margin: 0,
      }}
    >
      {children}
    </Component>
  );
}

export default CoreText;
